import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { Button, Input, Modal } from "../ui";
import { getStoredOwner } from "../../utils/authStorage";
import { PASSWORD_RULE } from "../../validators/authValidators";
import { toast } from "react-toastify";

const passwordField = Yup.string()
  .required("Password is required")
  .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,64}$/, PASSWORD_RULE);

export function extractLinkedinPublicIdentifier(raw: string): string | null {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return null;
  try {
    const withProtocol = trimmed.includes("://") ? trimmed : `https://${trimmed}`;
    const u = new URL(withProtocol);
    const match = u.pathname.match(/\/in\/([^/]+)\/?$/i) || u.pathname.match(/\/in\/([^/]+)/i);
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1].replace(/\/$/, ""));
  } catch {
    const m = trimmed.match(/linkedin\.com\/in\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1].replace(/\/$/, "")) : null;
  }
}

function toIdString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value !== null && "_id" in (value as object)) {
    return String((value as { _id?: unknown })._id ?? "").trim();
  }
  return String(value).trim();
}

export type ProductionMigrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** POST migration payload; should resolve with `{ msg }` on success. Axios errors surface in toast. */
  onSubmitMigration?: (payload: Record<string, unknown>) => Promise<unknown> | unknown;
};

const PROFILE_NOT_FOUND =
  "We could not match this LinkedIn profile to an existing record. Use Sign up to register a new account yourself—no new user is created from this form.";

export default function ProductionMigrationModal({
  isOpen,
  onClose,
  onSubmitMigration,
}: ProductionMigrationModalProps) {
  const [storedOwner, setStoredOwner] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const owner = getStoredOwner<Record<string, unknown> | null>();
    setStoredOwner(owner && typeof owner === "object" ? owner : null);
  }, [isOpen]);

  const ownerId = toIdString(storedOwner?._id);
  const ownerEmail = String(storedOwner?.email || "").trim();
  const ownerPublicIdentifier = String(storedOwner?.publicIdentifier || "").trim();
  const emailLocked = Boolean(ownerEmail);
  const linkedinPrefill = ownerPublicIdentifier
    ? `https://www.linkedin.com/in/${ownerPublicIdentifier}/`
    : "";

  const namePrefill = React.useMemo(() => {
    if (!storedOwner) return { firstName: "", lastName: "" };
    let firstName = String(storedOwner.firstName || "").trim();
    let lastName = String(storedOwner.lastName || "").trim();
    if (firstName || lastName) return { firstName, lastName };
    const full = String(storedOwner.name || "").trim();
    if (!full) return { firstName: "", lastName: "" };
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }, [storedOwner]);

  const initialValues = React.useMemo(
    () => ({
      linkedinProfileUrl: linkedinPrefill,
      email: ownerEmail,
      firstName: namePrefill.firstName,
      lastName: namePrefill.lastName,
      password: "",
      confirmPassword: "",
    }),
    [linkedinPrefill, ownerEmail, namePrefill, isOpen]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        linkedinProfileUrl: Yup.string()
          .trim()
          .required("LinkedIn profile URL is required")
          .test(
            "linkedin-url",
            "Enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/your-handle/)",
            (v) => Boolean(extractLinkedinPublicIdentifier(v || ""))
          ),
        email: emailLocked
          ? Yup.string().trim().email("Enter a valid email")
          : Yup.string().trim().email("Enter a valid email").required("Email is required"),
        firstName: Yup.string().trim().required("First name is required").max(50, "Max 50 characters"),
        lastName: Yup.string().trim().required("Last name is required").max(50, "Max 50 characters"),
        password: passwordField,
        confirmPassword: Yup.string()
          .required("Confirm password is required")
          .oneOf([Yup.ref("password")], "Passwords must match"),
      }),
    [emailLocked]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      const publicIdentifier = extractLinkedinPublicIdentifier(values.linkedinProfileUrl) || "";
      const email = emailLocked ? ownerEmail : values.email.trim();
      const payload = {
        linkedinProfileUrl: values.linkedinProfileUrl.trim(),
        publicIdentifier,
        email,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        password: values.password,
        ownerId: ownerId || undefined,
        userId: toIdString(storedOwner?.userId) || undefined,
      };

      try {
        if (onSubmitMigration) {
          const data = (await onSubmitMigration(payload)) as { msg?: string } | void;
          toast.success(typeof data?.msg === "string" && data.msg ? data.msg : "Your details were saved.");
          onClose();
        } else {
          toast.info("Add an onSubmitMigration handler to save this form.");
        }
      } catch (err: unknown) {
        const ax = err as { response?: { status?: number; data?: { msg?: string; message?: string; error?: string } } };
        const status = ax?.response?.status;
        const serverMsg =
          ax?.response?.data?.msg ||
          ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "";
        const combined = `${status} ${serverMsg}`.toLowerCase();
        if (status === 404) {
          toast.error(serverMsg || PROFILE_NOT_FOUND);
        } else if (
          combined.includes("not found") ||
          combined.includes("no owner") ||
          combined.includes("couldn't find") ||
          combined.includes("could not find")
        ) {
          toast.error(serverMsg || PROFILE_NOT_FOUND);
        } else {
          toast.error(serverMsg || "Something went wrong. Please try again.");
        }
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      title="Account setup"
      closeIconSrc="/icons/x_circle.svg"
      panelClassName="bg-white p-0"
      headerClassName="rounded-t-[12px] border border-border-grey border-b-0 bg-white px-4 pt-3"
      titleClassName="text-xl !font-medium !mt-0"
      bodyClassName="rounded-b-[12px] border border-t-0 border-border-grey bg-white px-4 py-4"
    >
      <p className="mb-4 text-sm text-dark-grey">
        Please enter your details below. We will complete your account setup so you can sign in smoothly next time.
      </p>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <Input
          id="migration-li-url"
          label="LinkedIn profile URL *"
          size="sm"
          name="linkedinProfileUrl"
          value={formik.values.linkedinProfileUrl}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="https://www.linkedin.com/in/your-public-id/"
          error={
            formik.touched.linkedinProfileUrl && formik.errors.linkedinProfileUrl
              ? String(formik.errors.linkedinProfileUrl)
              : ""
          }
        />

        <Input
          id="migration-email"
          label="Email *"
          type="email"
          size="sm"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="you@company.com"
          disabled={emailLocked}
          readOnly={emailLocked}
          error={formik.touched.email && formik.errors.email ? String(formik.errors.email) : ""}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            id="migration-fn"
            label="First name *"
            size="sm"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="First name"
            error={formik.touched.firstName && formik.errors.firstName ? String(formik.errors.firstName) : ""}
          />
          <Input
            id="migration-ln"
            label="Last name *"
            size="sm"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Last name"
            error={formik.touched.lastName && formik.errors.lastName ? String(formik.errors.lastName) : ""}
          />
        </div>

        <Input
          id="migration-pw"
          label="Password *"
          type="password"
          size="sm"
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Password"
          error={formik.touched.password && formik.errors.password ? String(formik.errors.password) : ""}
        />
        <Input
          id="migration-pw2"
          label="Confirm password *"
          type="password"
          size="sm"
          name="confirmPassword"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Confirm password"
          error={
            formik.touched.confirmPassword && formik.errors.confirmPassword
              ? String(formik.errors.confirmPassword)
              : ""
          }
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" isDisabled={formik.isSubmitting}>
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
}
